import {Constructor} from "../../global/constructor";
import Guid from "~/helpers/guid";
import {PermitteeType} from "~/enums/permitteeType";
import Vue from "vue";
import User from "~/models/user";
import ObjectModelEntry from "~/models/objectModelEntries/objectModelEntry";
import BuilderModel from "~/models/tracked/builderModel";
import {userActivationStatusDisplay} from "~/deactivatable";

export default function PermitteeAble<TBase extends Constructor<BuilderModel>>(Base: TBase) {
  return class extends Base {
    client_id: string = Guid.create();
    reference_id: string = null;
    reference_type: PermitteeType = null;
    _rootName: string

    name?: string;
    first_name?: string;
    last_name?: string;
    activation_status?: number;
    _display: () => string = () => this.client_id;

    setInDictionary() {
      Vue.set(this.parent[this.rootName], this.client_id, this);
    }

    get display() {
      return this._display();
    }

    get rootName(): string {
      return this._rootName
    }

    set display(funcOrString: Function | string) {
      if (typeof funcOrString === 'string') {
        this._display = () => funcOrString;
      } else {
        this._display = () => funcOrString(this);
      }
    }

    setDefaultDisplay() {
      switch (this.reference_type) {
        case PermitteeType.originalSubmitter:
          this.display = 'Original Submitter';
          break;
        case PermitteeType.entityOwner:
          this.display = 'Entity Owner';
          break;
        case PermitteeType.allApprovers:
          this.display = 'All Approvers';
          break;
        case PermitteeType.allCompleters:
          this.display = 'All Completers';
          break;
        case PermitteeType.stepCompleters:
          this.display = 'Current Step Completers';
          break;
        case PermitteeType.group:
          // using String so undefined turns into "undefined"
          this.display = () => String(this.name);
          break;
        case PermitteeType.relatedObjectOwner:
          this.display = () => `${this.name} (Related Record Holder)`
          break;
        case PermitteeType.objectOwner:
          this.display = 'Record Holder';
          break;
        case PermitteeType.user:
          this.display = () => `${userActivationStatusDisplay(this)}`;
          break;
        case PermitteeType.anyone:
          this.display = 'Anyone';
          break;
        case PermitteeType.accountRelation:
          this.display = () => String(this.name);
          break;
        case PermitteeType.entityAccountRelation:
          this.display = () => String(this.name);
          break;
      }
    }

    matches(user: User, entry: ObjectModelEntry): boolean {
      switch (this.reference_type) {
        case PermitteeType.objectOwner:
          return entry.owner && user.id === entry.owner.id;
        case PermitteeType.relatedObjectOwner:
          return entry.entryRelationsArray.some(er => er.relation.client_id === this.reference_id && er.relatedEntry.owner?.id === user.id) ||
            entry.inverseEntryRelationsArray.some(er => er.relation.client_id === this.reference_id && er.entry.owner?.id === user.id);
        case PermitteeType.user:
          return user.id === this.reference_id;
        case PermitteeType.group:
          return user.group_ids.includes(this.reference_id);
        case PermitteeType.anyone:
          return user !== null && user !== undefined;
        default:
          return false;
      }
    }

    isLegacyConfig(): boolean {
      return this.reference_type === PermitteeType.user;
    }

    get legacyConfigTooltip(): string | null {
      if (!this.isLegacyConfig()) return null;

      return 'This is a legacy configuration. Once you remove this user, you can only use undo to add this individual user back.';
    };
  };
}